import {GetStaticPaths, NextPageContext} from 'next';
import React from 'react';

import {FOUR_HOURS, RENDER_MODES} from '@udemy/nextjs-core';

import {AppContextData} from 'src/types/page-props';

import {ChangelogList} from './changelog-list.react-component';
import styles from './changelog.module.less';
import {DevHubPageFeature} from '../../features/dev-hub';
import log2307 from '../../generated/changelogs/2307.json';
import log2308 from '../../generated/changelogs/2308.json';
import log2309 from '../../generated/changelogs/2309.json';
import log2310 from '../../generated/changelogs/2310.json';
import log2311 from '../../generated/changelogs/2311.json';
import log2312 from '../../generated/changelogs/2312.json';
import log2402 from '../../generated/changelogs/2402.json';
import {getFullPageDevHubLayout} from '../../layouts/devhub-layout';
import {getStaticAppContext} from '../api/context.api';

import type {Change} from '@graphql-inspector/core';

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps = async (context: NextPageContext) => {
    const locale = context.locale as string;

    // Resolve appContext
    const [staticData] = await Promise.all([getStaticAppContext({locale})]);

    const appContext: AppContextData = Object.assign({}, staticData);
    return {
        props: {
            appContext,
            pageKey: 'dev_hub',
            locale: context.locale,
            renderMode: RENDER_MODES.ISR,
            translations: {},
        },
        revalidate: FOUR_HOURS,
    };
};

const IndexPage = () => {
    return (
        <DevHubPageFeature>
            <div className={styles['changelog-container']}>
                <nav>
                    <h2 className="ud-heading-xl">Changelog</h2>
                    <ul>
                        <li>
                            <a href="#february2024">February 2024</a>
                        </li>
                        <li>
                            <a href="#december2023">December 2023</a>
                        </li>
                        <li>
                            <a href="#november2023">November 2023</a>
                        </li>
                        <li>
                            <a href="#october2023">October 2023</a>
                        </li>
                        <li>
                            <a href="#september2023">September 2023</a>
                        </li>
                        <li>
                            <a href="#august2023">August 2023</a>
                        </li>
                        <li>
                            <a href="#july2023">July 2023</a>
                        </li>
                    </ul>
                </nav>
                <main>
                    <h1 className="ud-heading-serif-xxl">Changelog</h1>
                    <p>
                        This represents a log of all changes to the API and the versions that are
                        related to each new addition.
                    </p>
                    <p>
                        Any breaking changes will be released in a new API version. Breaking changes
                        are changes that can potentially break an integration. Breaking changes can
                        include:
                    </p>
                    <ul>
                        <li>Removing an entire operation</li>
                        <li>Removing or renaming a parameter</li>
                        <li>Removing or renaming a response field</li>
                        <li>Adding a new required parameter</li>
                        <li>Making a previously optional parameter required</li>
                        <li>Changing the type of a parameter or response field</li>
                        <li>Removing enum values</li>
                        <li>Adding a new validation rule to an existing parameter</li>
                        <li>Changing authentication or authorization requirements</li>
                    </ul>

                    <hr />
                    <ChangelogList
                        title="February 2024"
                        changes={log2402 as Change[]}
                        scrollId="february2024"
                    />
                    <ChangelogList
                        title="December 2023"
                        changes={log2312 as Change[]}
                        scrollId="december2023"
                    />
                    <ChangelogList
                        title="November 2023"
                        changes={log2311 as Change[]}
                        scrollId="november2023"
                    />
                    <ChangelogList
                        scrollId="october2023"
                        title="October 2023"
                        changes={log2310 as Change[]}
                    />
                    <ChangelogList
                        scrollId="september2023"
                        title="September 2023"
                        changes={log2309 as Change[]}
                    />
                    <ChangelogList
                        scrollId="august2023"
                        title="August 2023"
                        changes={log2308 as Change[]}
                    />
                    <ChangelogList
                        scrollId="july2023"
                        title="July 2023"
                        changes={log2307 as Change[]}
                    />
                </main>
            </div>
        </DevHubPageFeature>
    );
};

IndexPage.getLayout = getFullPageDevHubLayout;

export default IndexPage;
